import React, { useState } from "react";
import { Button, Box, Typography, CircularProgress } from "@mui/material";
import {
  WebAuthnDevice,
  useGetWebAuthnCredentialsQuery,
  usePostWebAuthnCredentialsMutation,
  useDeleteWebAuthnCredentialsMutation,
  useUpdateWebAuthnCredentialsMutation,
} from "src/store/features/api/generated";
import { useIsMobile } from "src/utils/hooks/useIsMobile";
import { DeviceManagementTable } from "./passkeyComponents/DeviceManagementTable";
import { ConfirmDialog } from "./passkeyComponents/ConfirmDialog";
import { MobilePasskeyView } from "./passkeyComponents/MobilePasskeyView";

// Main component
function WebAuthnManagement() {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState<string | null>(null);

  const {
    data: webauthnDevices,
    refetch,
    isLoading: isLoadingDevices,
    isFetching,
  } = useGetWebAuthnCredentialsQuery();
  const [postWebAuthn, { isLoading: isAddingDevice }] = usePostWebAuthnCredentialsMutation();
  const [deleteWebauthn, { isLoading: isDeletingDevice }] = useDeleteWebAuthnCredentialsMutation();
  const [updateWebAuthn, { isLoading: isUpdatingDevice }] = useUpdateWebAuthnCredentialsMutation();
  const isMobile = useIsMobile();

  const handleAddDevice = async () => {
    const encodedString = await window.tsPlatform.webauthn.register(
      sessionStorage.getItem("email"),
    );
    await postWebAuthn({ webAuthnCredentialsDto: { encodedString } });
    refetch();
  };

  const handleEditClick = (device: WebAuthnDevice) => {
    setEditingId(device.id);
    setEditName(device.displayName || "");
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editName.trim()) return;
    await updateWebAuthn({
      updateWebAuthnCredentialsDto: {
        credentialId: editingId,
        displayName: editName.trim(),
      },
    });
    setEditingId(null);
    setEditName("");
    refetch();
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName("");
  };

  const handleRemoveClick = (id: string) => {
    setDeviceToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleRemoveConfirm = async () => {
    if (!deviceToDelete) return;
    await deleteWebauthn({
      webAuthnDeleteCredentialsDto: {
        credentialId: deviceToDelete,
      },
    });
    setDeleteDialogOpen(false);
    setDeviceToDelete(null);
    refetch();
  };

  // Get unique devices by ID, taking only the first occurrence for unified passkeys
  const uniqueDevices =
    webauthnDevices?.devices.reduce((acc, device) => {
      if (!acc.find((d) => d.id === device.id)) {
        acc.push(device);
      }
      return acc;
    }, [] as WebAuthnDevice[]) || [];

  return (
    <Box
      sx={{
        width: "100%",
        position: "relative",
        border: "1px solid #E5E9EB",
        borderRadius: "20px",
        padding: "25px 28px",
        marginBottom: "30px",
      }}>
      {(isLoadingDevices || isFetching) && (
        <Box
          sx={{
            position: "absolute",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 1,
            borderRadius: "20px",
          }}>
          <CircularProgress />
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography style={{ fontWeight: 600, fontSize: "18px", lineHeight: "30px" }}>
          Passkey Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddDevice}
          disabled={isAddingDevice}
          size="small">
          {isAddingDevice ? <CircularProgress size={20} /> : "Add"}
        </Button>
      </Box>

      {isMobile ? (
        <MobilePasskeyView
          devices={uniqueDevices}
          editingId={editingId}
          editName={editName}
          onEditClick={handleEditClick}
          onSaveEdit={handleSaveEdit}
          onCancelEdit={handleCancelEdit}
          onEditNameChange={setEditName}
          onRemoveClick={handleRemoveClick}
          isUpdatingDevice={isUpdatingDevice}
          isDeletingDevice={isDeletingDevice}
        />
      ) : (
        <DeviceManagementTable
          devices={uniqueDevices}
          editingId={editingId}
          editName={editName}
          onEditClick={handleEditClick}
          onSaveEdit={handleSaveEdit}
          onCancelEdit={handleCancelEdit}
          onEditNameChange={setEditName}
          onRemoveClick={handleRemoveClick}
          isUpdatingDevice={isUpdatingDevice}
          isDeletingDevice={isDeletingDevice}
        />
      )}

      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleRemoveConfirm}
      />
    </Box>
  );
}

export default WebAuthnManagement;
